'use client';
import {initializeConsent} from './consent';
import {biteGa4Attribution} from './bite-ga4-attribution';
import {allowsBiteGa4} from './bite-ga4-policy';
import {CSRF_HEADER,getCsrfToken,invalidateCsrfToken} from './csrf';

// A site-local counting token, never an email or an advertising identifier.
const KEY='bite.count.v1';
type Counter={token:string;member:boolean;expires:number};
let volatile:Counter|null=null;
let audienceRequest:Promise<void>|null=null;
let resolvedAudience:'general'|'member'|null=null;
export function markBiteMember() {
  biteCountHeader();if(!volatile)return;
  volatile.member=true;try{localStorage.setItem(KEY,JSON.stringify(volatile));}catch{}
}
export function biteCountHeader() {
  if(typeof window==='undefined')return '';
  try {
    let saved:Counter|null=null;
    try{saved=JSON.parse(localStorage.getItem(KEY)||'null')||volatile;}catch{saved=volatile;}
    if(!saved||!/^[a-f0-9-]{36}$/.test(saved.token)||saved.expires<Date.now())
      saved={token:crypto.randomUUID(),member:false,expires:Date.now()+90*86400000};
    if(location.pathname==='/bite/membership')saved.member=true;
    volatile=saved;
    try{localStorage.setItem(KEY,JSON.stringify(saved));}catch{/* Tab-local count when storage is unavailable. */}
    let storedTest=false;try{storedTest=localStorage.getItem('bite_measurement_test')==='true';}catch{}
    const test=navigator.webdriver||/^(localhost|127\.0\.0\.1)$/.test(location.hostname)
      ||new URLSearchParams(location.search).get('bite_measurement_test')==='true'
      ||storedTest;
    return `${saved.token}:${saved.member?'member':'general'}:${test?'test':'live'}`;
  }catch{return '';}
}
export function biteAudience(): 'general'|'member' {
  return biteCountHeader().split(':')[1]==='member'?'member':'general';
}
const QUEUE='bite.count.pending.v3';
const ALLOWED=['visit','landing','search','login_start','completion_view','paywall','checkout_click','report_open','report_gift_view','report_gift_open','report_gift_close','report_offer_view','login_view','login_google','login_apple','login_success','login_failed','login_dismiss','checkout_request','checkout_redirect','checkout_error'];
type PendingCount={eventId:string;stage:string;occurredAt:string;header:string;details:Record<string,unknown>};
let pendingCounts:PendingCount[]=[];
let restored=false;
const sending=new Map<string,Promise<void>>();
function persistCounts(){try{localStorage.setItem(QUEUE,JSON.stringify(pendingCounts.slice(-100)));}catch{}}
export function checkoutFlowDetails(next:string) {
  try {
    const params=new URL(next,location.origin).searchParams;
    return {flow:params.get('flow')||'',label:params.get('cta')||'',report_offer:params.get('offer')==='report_once'?'report-first-monthly-20261013':''};
  }catch{return {};}
}
export function countBiteStage(stage='visit',details:Record<string,unknown>={}) {
  if(!ALLOWED.includes(stage))return Promise.resolve();
  const header=biteCountHeader();if(!header)return Promise.resolve();
  if(!restored){
    restored=true;
    try {pendingCounts=(JSON.parse(localStorage.getItem(QUEUE)||'[]') as PendingCount[]).filter(e=>ALLOWED.includes(e.stage)&&Date.now()-Date.parse(e.occurredAt)<86400000).slice(-100);}catch{pendingCounts=[];}
    for(const event of pendingCounts)void sendCount(event);
  }
  const event={eventId:crypto.randomUUID(),stage,occurredAt:new Date().toISOString(),header,
    details:{acquisition:biteGa4Attribution(),flow:details.flow,label:details.label,report_offer:details.report_offer,
      ...(stage==='login_success'&&details.measurement?{measurement:details.measurement}:{}),
      ...(stage==='completion_view'?{generationRequestId:details.generationRequestId,ticker:details.ticker}:{})}};
  pendingCounts.push(event);pendingCounts=pendingCounts.slice(-100);persistCounts();
  return sendCount(event);
}
function sendCount(event:PendingCount):Promise<void> {
  const current=sending.get(event.eventId);if(current)return current;
  const send=async(attempt=0):Promise<void>=>{
    try {
      await initializeConsent();
      const details={...event.details,acquisition:allowsBiteGa4()?event.details.acquisition:undefined};
      const csrf=await getCsrfToken();
      const response=await fetch('/api/analytics/bite-count',{method:'POST',credentials:'same-origin',keepalive:true,
        headers:{'x-bite-count':event.header,'Content-Type':'application/json',[CSRF_HEADER]:csrf},body:JSON.stringify({...event,details,header:undefined})});
      if(response.status===403&&attempt<3){invalidateCsrfToken();throw new Error('count_csrf_expired');}
      if(response.status>=400&&response.status<500){
        if(response.status===429)throw new Error('count_rate_limited');
        pendingCounts=pendingCounts.filter(e=>e.eventId!==event.eventId);persistCounts();return;
      }
      if(!response.ok)throw new Error('count_unavailable');
      const result=await response.json();
      if(result.cohort==='member'){markBiteMember();resolvedAudience='member';}
      else if(result.cohort==='general')resolvedAudience='general';
      pendingCounts=pendingCounts.filter(e=>e.eventId!==event.eventId);persistCounts();
    }catch{if(attempt<3){await new Promise<void>(resolve=>setTimeout(resolve,1000*2**attempt));await send(attempt+1);}}
  };
  const request=send().finally(()=>sending.delete(event.eventId));
  sending.set(event.eventId,request);return request;
}
export async function resolveBiteAudience() {
  const request=audienceRequest??=countBiteStage('visit');
  await request;
  // A retry is part of this request. A failed batch must not freeze future GA initialization.
  if(!resolvedAudience&&audienceRequest===request)audienceRequest=null;
  return biteAudience()==='member'?'member':resolvedAudience||'unknown';
}
export function countBiteArrival() {
  audienceRequest=countBiteStage('visit');
  if(location.pathname==='/'||location.pathname==='/bite')countBiteStage('landing');
}
